/**
 * SubgroupLeaderStats Component
 * 
 * Displays attendance statistics for subgroup leaders with two views:
 * 1. Personal Stats - Shows the leader's own attendance across all events
 * 2. Department Stats - Shows aggregate statistics for all members in the subgroup
 * 
 * Features:
 * - Dual tab interface for personal vs department stats
 * - Overall attendance rate card (blue for personal, purple for department)
 * - Monthly attendance breakdown with rates
 * - Event list with attendance details
 * - Bottom sheet to view individual member attendance for specific events
 * - Pull-to-refresh functionality
 * 
 * Key Calculations:
 * - Overall Rate: Total attendances by current members / Total PAST events only
 * - Department Stats: Uses only PAST events in selected month
 * - Confirmation Rate: Total confirmations / (Total possible = members × past events in month)
 * - Attended Rate: Total attendances / Total possible (past events only)
 * - Missed Rate: Total missed / Total possible (past events only)
 * - Declined Rate: Total declined / Total possible (past events only)
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../../components/LoadingSpinner'
import GroupLeaderBottomNav from '../../../components/GroupLeaderBottomNav'
import PullToRefresh from '../../../components/PullToRefresh'
import { 
  Bell,
  CalendarBlank,
  CalendarDots,
  CaretDown,
  Info,
  X,
} from '@phosphor-icons/react'

export default function GroupLeaderStats() {
  const navigate = useNavigate()
  const [userData, setUserData] = useState(null)
  const [subgroupData, setSubgroupData] = useState(null)
  const [subgroups, setSubgroups] = useState([]) // List of departments under the group
  const [selectedDepartment, setSelectedDepartment] = useState('all') // Department filter
  const [activeTab, setActiveTab] = useState('personal') // 'personal' or 'department'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth())
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [activeTooltip, setActiveTooltip] = useState(null) // Track which tooltip is open
  const [monthStats, setMonthStats] = useState({
    confirmed: 0,
    attended: 0,
    missed: 0,
    declined: 0,
    rate: 0,
    total: 0
  })
  const [departmentStats, setDepartmentStats] = useState({
    memberCount: 0,
    totalEvents: 0,
    confirmationRate: 0,
    attendedRate: 0,
    missedRate: 0,
    declinedRate: 0
  })
  const [overallRate, setOverallRate] = useState(0)
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [showBottomSheet, setShowBottomSheet] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [eventMemberDetails, setEventMemberDetails] = useState([])
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  useEffect(() => {
    loadStats()
  }, [selectedMonth, selectedYear, activeTab, selectedDepartment])

  useEffect(() => {
    // Close tooltip when clicking outside
    const handleClickOutside = () => setActiveTooltip(null)
    if (activeTooltip) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [activeTooltip])

  const loadStats = async () => {
    try {
      // ===== AUTHENTICATION & USER DATA =====
      const { data: { user } } = await supabase.auth.getUser()
      
      const { data: userDataResult } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()
      
      setUserData(userDataResult)

      // ===== GROUP INFORMATION =====
      // Get Group Leader's group (depth 2)
      const { data: groupMembership } = await supabase
        .from('group_members')
        .select('group_id, groups:group_id(id, name, depth)')
        .eq('user_id', user.id)
        .eq('groups.depth', 2)
        .maybeSingle()

      const groupId = groupMembership?.groups?.id
      setSubgroupData(groupMembership?.groups) // Still use subgroupData state for compatibility

      // Get all subgroups (departments) under this group
      if (groupId) {
        const { data: subgroupsData } = await supabase
          .from('groups')
          .select('id, name')
          .eq('parent_id', groupId)
          .eq('depth', 3)
          .order('name')

        setSubgroups(subgroupsData || [])
      }

      const now = new Date()
      
      // ===== DATE RANGE CALCULATION =====
      const monthStart = new Date(selectedYear, selectedMonth, 1)
      const monthEnd = new Date(selectedYear, selectedMonth + 1, 0)
      
      const monthStartStr = monthStart.toISOString().split('T')[0]
      const monthEndStr = monthEnd.toISOString().split('T')[0]

      // ===== FETCH EVENTS =====
      // Get past events only for selected month
      const today = new Date().toISOString().split('T')[0]
      const { data: monthEvents } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', userDataResult.org_id)
        .gte('start_date', monthStartStr)
        .lte('start_date', monthEndStr)
        .lte('start_date', today)  // Only past events
        .order('start_date', { ascending: false })

      // Get PAST events only for overall rate calculation
      const { data: allEvents } = await supabase
        .from('events')
        .select('id, start_date, end_time')
        .eq('org_id', userDataResult.org_id)
        .lte('start_date', today)

      if (activeTab === 'personal') {
        // ===== PERSONAL STATS PROCESSING =====
        // Get user's confirmations
        const { data: allConfirmations } = await supabase
          .from('event_confirmations')
          .select('*')
          .eq('user_id', user.id)

        // Get user's attendance records
        const { data: allAttendance } = await supabase
          .from('attendance_records')
          .select('*')
          .eq('user_id', user.id)

        // Calculate overall attendance rate using PAST events only
        const totalAllEvents = allEvents?.length || 0
        const totalAttended = allAttendance?.length || 0
        const overallAttendanceRate = totalAllEvents > 0 ? Math.round((totalAttended / totalAllEvents) * 100) : 0
        setOverallRate(overallAttendanceRate)

        // Process month events with status
        let confirmedCount = 0
        let attendedCount = 0
        let missedCount = 0
        let declinedCount = 0

        const now = new Date()
        const eventsWithStatus = await Promise.all(
          (monthEvents || []).map(async (event) => {
            const eventEnd = new Date(`${event.start_date}T${event.end_time}`)
            const isPast = eventEnd < now

            // Check attendance
            const attended = allAttendance?.find(a => a.event_id === event.id)
            
            if (attended) {
              attendedCount++
              return { ...event, status: 'attended' }
            }

            // Check confirmation
            const confirmation = allConfirmations?.find(c => c.event_id === event.id)
            
            if (isPast) {
              if (confirmation?.status === 'declined') {
                declinedCount++
                return { ...event, status: 'declined' }
              }
              missedCount++
              return { ...event, status: 'missed' }
            }

            if (confirmation) {
              if (confirmation.status === 'confirmed') {
                confirmedCount++
                return { ...event, status: 'confirmed' }
              }
              if (confirmation.status === 'declined') {
                declinedCount++
                return { ...event, status: 'declined' }
              }
            }

            return { ...event, status: 'no_response' }
          })
        )

        const totalMonthEvents = monthEvents?.length || 0
        const monthRate = totalMonthEvents > 0 ? Math.round((attendedCount / totalMonthEvents) * 100) : 0

        setMonthStats({
          confirmed: confirmedCount,
          attended: attendedCount,
          missed: missedCount,
          declined: declinedCount,
          rate: monthRate,
          total: totalMonthEvents
        })

        setEvents(eventsWithStatus)

      } else {
        // ===== SUB TEAM STATS PROCESSING =====
        
        // Early exit if no group
        if (!groupId) {
          setOverallRate(0)
          setMonthStats({
            confirmed: 0,
            attended: 0,
            missed: 0,
            declined: 0,
            rate: 0,
            total: 0
          })
          setDepartmentStats({
            memberCount: 0,
            totalEvents: 0,
            confirmationRate: 0,
            attendedRate: 0,
            missedRate: 0,
            declinedRate: 0
          })
          setEvents([])
          return
        }

        // Determine which subgroups to query based on department filter
        let targetSubgroupIds = []
        if (selectedDepartment === 'all') {
          targetSubgroupIds = subgroups.map(sg => sg.id)
        } else {
          targetSubgroupIds = [selectedDepartment]
        }

        if (targetSubgroupIds.length === 0) {
          setOverallRate(0)
          setMonthStats({
            confirmed: 0,
            attended: 0,
            missed: 0,
            declined: 0,
            rate: 0,
            total: 0
          })
          setDepartmentStats({
            memberCount: 0,
            totalEvents: 0,
            confirmationRate: 0,
            attendedRate: 0,
            missedRate: 0,
            declinedRate: 0
          })
          setEvents([])
          return
        }

        // Fetch members from filtered department(s)
        const { data: filteredMembers } = await supabase
          .from('group_members')
          .select('user_id')
          .in('group_id', targetSubgroupIds)

        let memberIds = filteredMembers?.map(m => m.user_id) || []
        
        // Include the Group Leader if "All Departments" is selected
        if (selectedDepartment === 'all' && groupId) {
          const { data: groupLeader } = await supabase
            .from('group_members')
            .select('user_id')
            .eq('group_id', groupId)
          
          if (groupLeader && groupLeader.length > 0) {
            memberIds = [...memberIds, ...groupLeader.map(gl => gl.user_id)]
          }
        }
        
        const memberCount = memberIds.length

        // CALCULATE OVERALL RATE FOR ENTIRE GROUP (not filtered)
        const allSubgroupIds = subgroups.map(sg => sg.id)
        const { data: allGroupMembers } = await supabase
          .from('group_members')
          .select('user_id')
          .in('group_id', allSubgroupIds)

        let allGroupMemberIds = allGroupMembers?.map(m => m.user_id) || []
        
        // Always include Group Leader in overall rate calculation
        if (groupId) {
          const { data: groupLeader } = await supabase
            .from('group_members')
            .select('user_id')
            .eq('group_id', groupId)
          
          if (groupLeader && groupLeader.length > 0) {
            allGroupMemberIds = [...allGroupMemberIds, ...groupLeader.map(gl => gl.user_id)]
          }
        }
        
        const { data: allGroupAttendance } = await supabase
          .from('attendance_records')
          .select('*')
          .in('user_id', allGroupMemberIds)

        const groupTotalEvents = allEvents?.length || 0
        const groupPossibleAttendances = groupTotalEvents * allGroupMemberIds.length
        const groupTotalAttendances = allGroupAttendance?.length || 0
        const groupOverallRate = groupPossibleAttendances > 0 
          ? Math.round((groupTotalAttendances / groupPossibleAttendances) * 100) 
          : 0

        setOverallRate(groupOverallRate)

        // Early exit if no members in filtered department(s)
        if (memberCount === 0) {
          // No members in filtered department(s), but still show events
          setMonthStats({
            confirmed: 0,
            attended: 0,
            missed: 0,
            declined: 0,
            rate: 0,
            total: monthEvents?.length || 0
          })
          setDepartmentStats({
            memberCount: 0,
            totalEvents: 0,
            confirmationRate: 0,
            attendedRate: 0,
            missedRate: 0,
            declinedRate: 0
          })
          
          // Still show events, just with 0 attendance
          const eventsWithZeroAttendance = (monthEvents || []).map(event => ({
            ...event,
            attended: 0,
            confirmed: 0,
            declined: 0,
            status: 'aggregate'
          }))
          
          setEvents(eventsWithZeroAttendance)
          // Don't return - continue to show events
        } else {
          // Has members - proceed with normal calculations
          // Fetch confirmations and attendance for FILTERED members
          const { data: allConfirmations } = await supabase
            .from('event_confirmations')
            .select('*')
            .in('user_id', memberIds)

          const { data: allAttendance } = await supabase
            .from('attendance_records')
            .select('*')
            .in('user_id', memberIds)

          // Process month events with aggregate stats
          let confirmedCount = 0
          let attendedCount = 0
          let missedCount = 0
          let declinedCount = 0

          const eventsWithStatus = (monthEvents || []).map((event) => {
            const eventEnd = new Date(`${event.start_date}T${event.end_time}`)
            const isPast = eventEnd < now

            // Count attendances for this event
            const eventAttendances = allAttendance?.filter(a => a.event_id === event.id) || []
            attendedCount += eventAttendances.length

            // Count confirmations for this event
            const eventConfirmations = allConfirmations?.filter(c => c.event_id === event.id) || []
            const confirmedMembers = eventConfirmations.filter(c => c.status === 'confirmed').length
            const declinedMembers = eventConfirmations.filter(c => c.status === 'declined').length
            
            confirmedCount += confirmedMembers
            declinedCount += declinedMembers

            // Calculate missed for past events
            if (isPast) {
              const missedMembers = memberCount - eventAttendances.length - declinedMembers
              missedCount += missedMembers
            }

            return { 
              ...event, 
              attended: eventAttendances.length,
              confirmed: confirmedMembers,
              declined: declinedMembers,
              status: 'aggregate' 
            }
          })

          const totalMonthEvents = monthEvents?.length || 0
          const totalMonthPossible = totalMonthEvents * memberCount
          const monthRate = totalMonthPossible > 0 ? Math.round((attendedCount / totalMonthPossible) * 100) : 0

          // Calculate rates for department view
          const confirmationRate = totalMonthPossible > 0 ? Math.round((confirmedCount / totalMonthPossible) * 100) : 0
          const attendedRate = totalMonthPossible > 0 ? Math.round((attendedCount / totalMonthPossible) * 100) : 0
          const missedRate = totalMonthPossible > 0 ? Math.round((missedCount / totalMonthPossible) * 100) : 0
          const declinedRate = totalMonthPossible > 0 ? Math.round((declinedCount / totalMonthPossible) * 100) : 0

          setDepartmentStats({
            memberCount,
            totalEvents: totalMonthEvents,
            confirmationRate,
            attendedRate,
            missedRate,
            declinedRate
          })

          setMonthStats({
            confirmed: confirmedCount,
            attended: attendedCount,
            missed: missedCount,
            declined: declinedCount,
            rate: monthRate,
            total: totalMonthEvents
          })

          setEvents(eventsWithStatus)
        }
      }

    } catch (error) {
      console.error('Load stats error:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTooltipText = (type) => {
    // Dynamic text based on filter: "sub team" when all, "department" when specific
    const contextText = selectedDepartment === 'all' ? 'sub team' : 'department'
    
    const tooltips = {
      totalMembers: `Total number of members in your ${contextText}`,
      totalEvents: `Total events in ${months[selectedMonth]} ${selectedYear}`,
      confirmationRate: 'Percentage of members who confirmed attendance across all events',
      attendedRate: 'Percentage of members who actually attended events',
      missedRate: 'Percentage of members who did not attend or respond',
      declinedRate: 'Percentage of members who declined to attend'
    }
    return tooltips[type] || ''
  }

  const handleEventClick = async (event) => {
    // Only work for department tab
    if (activeTab !== 'department') return
    
    setSelectedEvent(event)
    setShowBottomSheet(true)
    setLoadingMembers(true)
    
    try {
      // ===== GET GROUP ID =====
      const { data: { user } } = await supabase.auth.getUser()
      
      const { data: groupMembership } = await supabase
        .from('group_members')
        .select('group_id, groups:group_id(id, name, depth)')
        .eq('user_id', user.id)
        .eq('groups.depth', 2)
        .maybeSingle()
      
      const groupId = groupMembership?.groups?.id
      
      if (!groupId) {
        setLoadingMembers(false)
        return
      }
      
      // ===== DETERMINE WHICH SUBGROUPS TO QUERY BASED ON FILTER =====
      let targetSubgroupIds = []
      if (selectedDepartment === 'all') {
        targetSubgroupIds = subgroups.map(sg => sg.id)
      } else {
        targetSubgroupIds = [selectedDepartment]
      }
      
      if (targetSubgroupIds.length === 0) {
        setLoadingMembers(false)
        return
      }
      
      // ===== FETCH MEMBERS FROM FILTERED DEPARTMENT(S) =====
      const { data: filteredMembers } = await supabase
        .from('group_members')
        .select('user_id')
        .in('group_id', targetSubgroupIds)
      
      let memberIds = filteredMembers?.map(m => m.user_id) || []
      
      // Include Group Leader if "All Departments" is selected
      if (selectedDepartment === 'all') {
        const { data: groupLeader } = await supabase
          .from('group_members')
          .select('user_id')
          .eq('group_id', groupId)
        
        if (groupLeader && groupLeader.length > 0) {
          memberIds = [...memberIds, ...groupLeader.map(gl => gl.user_id)]
        }
      }
      
      if (memberIds.length === 0) {
        setEventMemberDetails([])
        setLoadingMembers(false)
        return
      }
      
      // ===== FETCH USER DETAILS =====
      const { data: users } = await supabase
        .from('users')
        .select('id, name')
        .in('id', memberIds)
      
      // ===== FETCH EVENT DATA =====
      // Get attendance records for this event
      const { data: attendanceRecords } = await supabase
        .from('attendance_records')
        .select('user_id')
        .eq('event_id', event.id)
      
      // Get confirmations for this event
      const { data: confirmations } = await supabase
        .from('event_confirmations')
        .select('user_id, status')
        .eq('event_id', event.id)
      
      // ===== DETERMINE EVENT STATUS =====
      const now = new Date()
      const eventEnd = new Date(`${event.start_date}T${event.end_time}`)
      const isPast = eventEnd < now
      
      // ===== BUILD MEMBER DETAILS WITH STATUS =====
      const memberDetails = memberIds.map(memberId => {
        const user = users?.find(u => u.id === memberId)
        const hasAttended = attendanceRecords?.some(a => a.user_id === memberId)
        const confirmation = confirmations?.find(c => c.user_id === memberId)
        
        // Determine status based on attendance and confirmations
        let status = 'no_response'
        
        if (hasAttended) {
          status = 'attended'
        } else if (confirmation?.status === 'declined') {
          status = 'declined'
        } else if (isPast) {
          status = 'missed'
        } else if (confirmation?.status === 'confirmed') {
          status = 'confirmed'
        }
        
        return {
          id: memberId,
          name: user?.name || 'Unknown',
          status
        }
      })
      
      setEventMemberDetails(memberDetails)
    } catch (error) {
      console.error('Error loading member details:', error)
    } finally {
      setLoadingMembers(false)
    }
  }

  const closeBottomSheet = () => {
    setIsClosing(true)
    setTimeout(() => {
      setShowBottomSheet(false)
      setIsClosing(false)
    }, 300) // Match animation duration
  }

  const getStatusBadge = (status) => {
    if (status === 'attended') {
      return (
        <span className="bg-[#eaf5ed] text-[#2b9646] px-3 py-1 rounded-full text-xs font-normal">
          Attended
        </span>
      )
    }
    if (status === 'missed') {
      return (
        <span className="bg-[#fff4ed] text-[#d97706] px-3 py-1 rounded-full text-xs font-normal">
          Missed
        </span>
      )
    }
    if (status === 'declined') {
      return (
        <span className="bg-[#fff4ed] text-[#d97706] px-3 py-1 rounded-full text-xs font-normal">
          Declined
        </span>
      )
    }
    if (status === 'confirmed') {
      return (
        <span className="bg-[#eaf5ed] text-[#2b9646] px-3 py-1 rounded-full text-xs font-normal">
          Confirmed
        </span>
      )
    }
    return (
      <span className="bg-slate-50 text-slate-500 px-3 py-1 rounded-full text-xs font-normal">
        No response
      </span>
    )
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  if (loading) {
    return <LoadingSpinner message="Loading statistics..." />
  }

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        @keyframes slideDown {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(100%);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out both;
        }

        .animate-fadeOut {
          animation: fadeOut 0.3s ease-out both;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out both;
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease-out both;
        }
      `}</style>
      <PullToRefresh onRefresh={loadStats}>
        <div className="bg-white min-h-screen pb-20">
          {/* Header */}
          <div className="bg-white px-6 pt-10 pb-4">
            <div className="flex justify-between items-center">
              <h1 className="text-2xl font-semibold text-[#1E293B]">
                {activeTab === 'personal' ? 'Stats' : 'Stats'}
              </h1>
              <button className="w-10 h-10 flex items-center justify-center bg-slate-50 border border-slate-200 rounded-[20px]">
                <Bell size={20} weight="fill" className="text-[#64748B]" />
              </button>
            </div>
          </div>

          {/* Segmented Control */}
          <div className="px-6 pb-4">
            <div className="bg-slate-50 border border-slate-200 rounded-[15px] p-1 flex gap-1">
              <button
                onClick={() => setActiveTab('personal')}
                className={`flex-1 px-4 py-2.5 rounded-[12px] text-sm font-regular transition-all ${
                  activeTab === 'personal'
                    ? 'bg-[#D6e1ff] text-[#1E293B] shadow-sm'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                My Stats
              </button>
              <button
                onClick={() => setActiveTab('department')}
                className={`flex-1 px-4 py-2.5 rounded-[12px] text-sm font-regular transition-all ${
                  activeTab === 'department'
                    ? 'bg-[#D6e1ff] text-[#1E293B] shadow-sm'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                My Sub team's
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="px-6 pb-6">
            <div className="flex gap-3">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-[15px] text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3167fe]"
              >
                {months.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>

              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-[15px] text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#3167fe]"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 space-y-4">
            {/* Overall Attendance Rate Card */}
            <div 
              className="rounded-[25px] p-6 text-white relative overflow-hidden"
              style={{
                backgroundColor: activeTab === 'personal' ? '#2248B2' : '#2B9646'
              }}
            >
              {/* Pattern overlay */}
              <div 
                className="absolute inset-0 opacity-40"
                style={{
                  backgroundImage: activeTab === 'personal' ? 'url(/Line.png)' : 'url(/Line2.png)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              ></div>

              {/* Content - needs to be above pattern */}
              <div className="relative z-10">
                <div className="absolute top-0 left-0">
                  <div className="">
                    <CalendarDots size={40} weight="fill" className={activeTab === 'personal' ? 'text-[#ADC2FF]' : 'text-[#bfe0c8]'} />
                  </div>
                </div>
                
                <div className="pt-20 pb-4 text-left">
                  <div className={`text-7xl font-bold mb-3 leading-none ${activeTab === 'personal' ? 'text-[#D6E1FF]' : 'text-[#bfe0c8]'}`}>
                    {overallRate}%
                  </div>
                  <p className={`text-xl ${activeTab === 'personal' ? 'text-[#ADC2FF]' : 'text-[#bfe0c8]'}`}>
                    {activeTab === 'personal' ? (
                      <>My overall<br/>attendance rate</>
                    ) : subgroupData?.name ? (
                      <>{subgroupData.name}'s overall<br/>attendance rate</>
                    ) : (
                      <>Group's overall<br/>attendance rate</>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Department Filter - Only show in department tab */}
            {activeTab === 'department' && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">Filter by Department</label>
                <div className="relative w-full">
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="appearance-none w-full bg-white border border-slate-300 rounded-lg px-3 py-2 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Departments</option>
                    {subgroups.map(sg => (
                      <option key={sg.id} value={sg.id}>{sg.name}</option>
                    ))}
                  </select>
                  <CaretDown size={16} weight="regular" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-600" />
                </div>
              </div>
            )}

            {/* Month Stats Card */}
            <div className="bg-white rounded-[20px] border border-slate-200 p-5">
              <h2 className="text-xl  text-[#475569] font-semibold text-slate-800 mb-4">
                Stats - {months[selectedMonth]}, {selectedYear}
              </h2>
              
              {/* Attendance rate */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-base text-[#1E293B] font-light ">Attendance rate</span>
                  <span className="text-base text-[#1E293B] font-bold ">{monthStats.rate}%</span>
                </div>
                
                <div className="w-full bg-[#EAF0FF] rounded-full h-6 mb-2">
                  <div 
                    className="bg-[#3167FE] h-6 rounded-full transition-all duration-500"
                    style={{ width: `${monthStats.rate}%` }}
                  ></div>
                </div>
                
                <p className="text-sm text-[#475569] mb-10" >
                  <span className="font-semibold text-[#1E293B]">{monthStats.attended} of {monthStats.total}</span> Total event(s)
                </p>
              </div>

              {/* Stats list - Different for personal vs department */}
              {activeTab === 'personal' ? (
                <div className="space-y-3 mt-4">
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#3167fe]"></div>
                      <span className="text-sm text-[#475569]">Events Confirmed</span>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{monthStats.confirmed}</span>
                  </div>

                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#2b9646]"></div>
                      <span className="text-sm text-[#475569]">Events Attended</span>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{monthStats.attended}</span>
                  </div>

                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#F26D35]"></div>
                      <span className="text-sm text-[#475569]">Events Missed</span>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{monthStats.missed}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#D00E0E]"></div>
                      <span className="text-sm text-[#475569]">Events Declined</span>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{monthStats.declined}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 mt-4">
                  {/* Total Members */}
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#3167fe]"></div>
                      <span className="text-sm text-[#475569]">Total Members</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setActiveTooltip(activeTooltip === 'totalMembers' ? null : 'totalMembers')
                        }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'totalMembers' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('totalMembers')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{departmentStats.memberCount}</span>
                  </div>

                  {/* Total Events */}
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#3167fe]"></div>
                      <span className="text-sm text-[#475569]">Total events</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'totalEvents' ? null : 'totalEvents') }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'totalEvents' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('totalEvents')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{departmentStats.totalEvents}</span>
                  </div>

                  {/* Confirmation Rate */}
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#3167fe]"></div>
                      <span className="text-sm text-[#475569]">Confirmation rate</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'confirmationRate' ? null : 'confirmationRate') }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'confirmationRate' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('confirmationRate')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{departmentStats.confirmationRate}%</span>
                  </div>

                  {/* Attended Rate */}
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#2b9646]"></div>
                      <span className="text-sm text-[#475569]">Attended rate</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'attendedRate' ? null : 'attendedRate') }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'attendedRate' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('attendedRate')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{departmentStats.attendedRate}%</span>
                  </div>

                  {/* Missed Rate */}
                  <div className="flex items-center justify-between gap-3 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#F26D35]"></div>
                      <span className="text-sm text-[#475569]">Missed rate</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'missedRate' ? null : 'missedRate') }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'missedRate' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('missedRate')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{departmentStats.missedRate}%</span>
                  </div>

                  {/* Declined Rate */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#D00E0E]"></div>
                      <span className="text-sm text-[#475569]">Declined rate</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveTooltip(activeTooltip === 'declinedRate' ? null : 'declinedRate') }}
                        className="text-slate-400 hover:text-slate-600 transition-colors relative"
                      >
                        <Info size={16} weight="fill" />
                        {activeTooltip === 'declinedRate' && (
                          <div className="absolute left-0 top-6 bg-slate-800 text-white text-xs rounded-lg py-2 px-3 w-48 z-50 shadow-lg">
                            {getTooltipText('declinedRate')}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-slate-800 transform rotate-45"></div>
                          </div>
                        )}
                      </button>
                    </div>
                    <span className="font-semibold text-[#1E293B]">{departmentStats.declinedRate}%</span>
                  </div>
                </div>
              )}
            </div>

            {/* Events List Card */}
            <div className="bg-white rounded-[20px] border border-slate-200 p-5">
              <h2 className="text-xl font-semibold text-[#475569] mb-4">
                Events - {months[selectedMonth]}, {selectedYear}
              </h2>
              
              {events.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">
                  No events in {months[selectedMonth]} {selectedYear}
                </p>
              ) : (
                <div className="space-y-3">
                  {events.map((event) => (
                    <div 
                      key={event.id}
                      onClick={() => activeTab === 'department' && handleEventClick(event)}
                      className={`flex items-center gap-3 py-3 border-b border-slate-100 last:border-0 ${
                        activeTab === 'department' ? 'cursor-pointer hover:bg-slate-50 transition-colors rounded-lg px-2 -mx-2' : ''
                      }`}
                    >
                      <div className="bg-slate-100 rounded-[12px] p-2 flex-shrink-0">
                        <CalendarBlank size={24} weight="fill" className="text-slate-600" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{event.title}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(event.start_date).toLocaleDateString('en-US', { 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </p>
                      </div>
                      
                      {activeTab === 'personal' ? (
                        getStatusBadge(event.status)
                      ) : (
                        <div className="text-right">
                          <p className="text-sm font-semibold text-slate-800">
                            {event.attended || 0} attended
                          </p>
                          <p className="text-xs text-slate-500">
                            out of {departmentStats.memberCount} member(s)
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </PullToRefresh>
      
      {/* Bottom Sheet for Event Attendance Details */}
      {showBottomSheet && (
        <>
          {/* Backdrop with conditional fade animation - stops above bottom nav */}
          <div 
            className={`fixed inset-0 bottom-20 bg-black/50 z-[60] ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}
            onClick={closeBottomSheet}
          />
          
          {/* Bottom Sheet with smooth slide animation */}
          <div 
            className={`fixed inset-x-0 bottom-0 z-[70] bg-white rounded-t-[24px] shadow-xl max-h-[70vh] overflow-hidden 
            ${!isClosing ? 'animate-slideUp' : 'animate-slideDown'}`}
            style={!isClosing ? { transform: 'translateY(100%)' } : undefined}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">Event attendance</h3>
              <button 
                onClick={closeBottomSheet}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X size={24} className="text-slate-600" />
              </button>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(70vh-80px)] pb-24">
              {loadingMembers ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
                    <p className="text-sm text-slate-600">
                      <span className="font-semibold text-slate-800">
                        {eventMemberDetails.filter(m => m.status === 'attended').length}
                      </span>
                      {' '}of{' '}
                      <span className="font-semibold text-slate-800">
                        {eventMemberDetails.length}
                      </span>
                      {' '}members attended
                    </p>
                    {selectedEvent && (
                      <p className="text-xs text-slate-500 mt-1">
                        {selectedEvent.title} • {new Date(selectedEvent.start_date).toLocaleDateString('en-US', { 
                          month: 'long', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    )}
                  </div>
                  
                  {/* Member List */}
                  <div className="p-5 space-y-3">
                    {eventMemberDetails.map((member) => (
                      <div 
                        key={member.id}
                        className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {member.name}
                          </p>
                        </div>
                        
                        <div className="ml-3">
                          {getStatusBadge(member.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
      
      <GroupLeaderBottomNav />
    </>
  )
}